// src/pages/ProfessionalDetails.tsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Stethoscope,
  ShieldCheck,
  Globe,
  GraduationCap,
  Building2,
  ArrowRight,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';

interface FormState {
  specialization: string;
  licensingBody: string;
  practiceCountry: string;
  qualification: string;
  clinicHospitalName: string;
}

const ProfessionalDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const state = (location.state || {}) as {
    userType?: string;
    email?: string;
    registrationToken?: string;
  };

  const { email, registrationToken } = state;

  const [form, setForm] = useState<FormState>({
    specialization: '',
    licensingBody: '',
    practiceCountry: '',
    qualification: '',
    clinicHospitalName: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (!registrationToken) {
      navigate('/register', { replace: true });
    }
  }, [registrationToken, navigate]);

  if (!registrationToken) return null;

  const required: Array<{ key: keyof FormState; label: string }> = [
    { key: 'specialization', label: 'Specialization' },
    { key: 'licensingBody', label: 'Licensing Body' },
    { key: 'practiceCountry', label: 'Practice Country' },
    { key: 'qualification', label: 'Qualification' },
  ];

  const handleChange = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');

    const nextErrors: Partial<Record<keyof FormState, string>> = {};
    for (const { key, label } of required) {
      if (!form[key].trim()) nextErrors[key] = `${label} is required`;
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    toast.success('Details saved');
    navigate('/register/select-plan', {
      state: {
        userType: 'doctor',
        email,
        registrationToken,
        specialization: form.specialization.trim(),
        licensingBody: form.licensingBody.trim(),
        practiceCountry: form.practiceCountry.trim(),
        qualification: form.qualification.trim(),
        clinicHospitalName: form.clinicHospitalName.trim() || undefined,
      },
    });
  };

  const inputClass = (field: keyof FormState, extra = '') =>
    `w-full h-[40px] sm:h-[42px] bg-white rounded-md pl-10 pr-3.5 text-[14px] text-[#1E293B] placeholder-[#94A3B8] outline-none shadow-sm transition-all border ${
      errors[field]
        ? 'border-red-300 focus:ring-2 focus:ring-red-400'
        : 'border-white/40 focus:ring-2 focus:ring-[#097173]'
    } ${extra}`;

  const fieldError = (field: keyof FormState) =>
    errors[field] ? (
      <p className="mt-1 text-xs text-red-100 font-medium">{errors[field]}</p>
    ) : null;

  return (
    <div className="relative w-screen min-h-screen md:h-screen flex flex-col md:flex-row overflow-x-hidden md:overflow-hidden bg-white">
      {/* Asymmetric Curved Divider SVG (desktop) */}
      <div className="absolute inset-0 w-full h-full pointer-events-none z-[1] hidden md:block" aria-hidden="true">
        <svg className="w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="none">
          <defs>
            <linearGradient id="profTealGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#26ABB0" />
              <stop offset="100%" stopColor="#219FA4" />
            </linearGradient>
          </defs>
          <path
            d="M 526.3 0 C 464.5 333.3, 475.4 666.7, 602.3 1000 L 1000 1000 L 1000 0 Z"
            fill="url(#profTealGrad)"
          />
        </svg>
      </div>

      {/* LEFT PANEL: Branding */}
      <div className="relative w-full md:w-1/2 h-auto md:h-full flex flex-col justify-between px-6 py-4 sm:p-11 lg:p-14 z-[2] bg-white md:bg-transparent shrink-0">
        <div className="my-3 sm:my-4 md:my-0 md:absolute md:top-[43%] md:left-[50%] md:-translate-x-1/2 md:-translate-y-1/2 flex flex-col items-center justify-center text-center w-full max-w-[90%] md:max-w-[720px] lg:max-w-[820px] xl:max-w-[880px] px-2">
          <img
            src="/logo/DiagnomIQ-01.svg"
            alt="DiagnomIQ - Precision insights for every patient"
            className="w-full max-w-[320px] sm:max-w-[460px] md:max-w-[640px] lg:max-w-[740px] xl:max-w-[800px] h-auto object-contain"
          />
        </div>
      </div>

      {/* RIGHT PANEL: Professional details form on Teal Background (no visible scrollbar) */}
      <div className="relative w-full md:w-1/2 flex-1 md:h-full overflow-y-auto z-[2] bg-[#23A4A8] md:bg-transparent rounded-t-[32px] md:rounded-none shadow-lg md:shadow-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {/* Top-Right Decorative Arcs */}
        <svg
          className="hidden md:block absolute top-0 right-0 w-[300px] lg:w-[420px] h-[300px] lg:h-[420px] pointer-events-none z-[1]"
          viewBox="0 0 450 450"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="492" cy="-25" r="225" fill="#2FAFB1" />
          <circle cx="492" cy="-25" r="162" fill="#48B9BC" />
        </svg>

        {/* Bottom-Right Decorative Arcs */}
        <svg
          className="hidden md:block absolute bottom-0 right-0 w-[340px] lg:w-[500px] h-[340px] lg:h-[500px] pointer-events-none z-[1]"
          viewBox="0 0 550 550"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="578" cy="651" r="298" fill="#33AAAD" />
          <circle cx="578" cy="651" r="244" fill="#44B6B9" />
        </svg>

        <div className="min-h-full w-full flex flex-col justify-center items-center px-5 sm:px-8 lg:px-12 py-5 sm:py-6">
          <div className="relative z-[3] w-full max-w-[460px]">
            <h1 className="text-[22px] sm:text-[25px] font-bold text-white tracking-tight leading-tight mb-1">
              Professional Details
            </h1>
            <p className="text-[13px] sm:text-[13.5px] font-normal text-[#E0F7F6] mb-3 leading-relaxed">
              Tell us about your practice to complete your profile
              {email ? <> for <span className="font-semibold text-white">{email}</span></> : null}.
            </p>

            {apiError && (
              <div className="flex items-center space-x-2 text-white bg-red-600/40 border border-red-300/40 p-2.5 rounded-md text-xs sm:text-sm mb-3.5">
                <AlertCircle className="h-4.5 w-4.5 shrink-0 text-red-200" />
                <span>{apiError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-2.5" noValidate>
              {/* Specialization */}
              <div>
                <label htmlFor="specialization" className="block text-[13px] font-semibold text-white mb-1">
                  Specialization
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                    <Stethoscope className="h-4 w-4" />
                  </span>
                  <input
                    id="specialization"
                    name="specialization"
                    type="text"
                    value={form.specialization}
                    onChange={(e) => handleChange('specialization', e.target.value)}
                    className={inputClass('specialization')}
                    placeholder="e.g. Oncology"
                  />
                </div>
                {fieldError('specialization')}
              </div>

              {/* Licensing Body & Practice Country */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label htmlFor="licensingBody" className="block text-[13px] font-semibold text-white mb-1">
                    Licensing Body
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                      <ShieldCheck className="h-4 w-4" />
                    </span>
                    <input
                      id="licensingBody"
                      name="licensingBody"
                      type="text"
                      value={form.licensingBody}
                      onChange={(e) => handleChange('licensingBody', e.target.value)}
                      className={inputClass('licensingBody')}
                      placeholder="e.g. GMC"
                    />
                  </div>
                  {fieldError('licensingBody')}
                </div>
                <div>
                  <label htmlFor="practiceCountry" className="block text-[13px] font-semibold text-white mb-1">
                    Practice Country
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                      <Globe className="h-4 w-4" />
                    </span>
                    <input
                      id="practiceCountry"
                      name="practiceCountry"
                      type="text"
                      value={form.practiceCountry}
                      onChange={(e) => handleChange('practiceCountry', e.target.value)}
                      className={inputClass('practiceCountry')}
                      placeholder="e.g. United Kingdom"
                    />
                  </div>
                  {fieldError('practiceCountry')}
                </div>
              </div>

              {/* Qualification */}
              <div>
                <label htmlFor="qualification" className="block text-[13px] font-semibold text-white mb-1">
                  Qualification
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                    <GraduationCap className="h-4 w-4" />
                  </span>
                  <input
                    id="qualification"
                    name="qualification"
                    type="text"
                    value={form.qualification}
                    onChange={(e) => handleChange('qualification', e.target.value)}
                    className={inputClass('qualification')}
                    placeholder="e.g. MBBS, MD"
                  />
                </div>
                {fieldError('qualification')}
              </div>

              {/* Clinic / Hospital Name (optional) */}
              <div>
                <label htmlFor="clinicHospitalName" className="block text-[13px] font-semibold text-white mb-1">
                  Clinic / Hospital Name <span className="text-white/50 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none">
                    <Building2 className="h-4 w-4" />
                  </span>
                  <input
                    id="clinicHospitalName"
                    name="clinicHospitalName"
                    type="text"
                    value={form.clinicHospitalName}
                    onChange={(e) => handleChange('clinicHospitalName', e.target.value)}
                    className={inputClass('clinicHospitalName')}
                    placeholder="e.g. St. Mary's Hospital"
                  />
                </div>
              </div>

              <div className="pt-1.5">
                <button
                  type="submit"
                  aria-label="Continue to plan selection"
                  className="w-full h-[42px] sm:h-[44px] bg-[#097173] hover:bg-[#075E60] active:scale-[0.99] text-white font-semibold text-[14.5px] sm:text-[15px] rounded-md flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transition-all duration-150 cursor-pointer"
                >
                  <span>Continue to Plan Selection</span>
                  <ArrowRight className="h-4.5 w-4.5 ml-1" />
                </button>
              </div>

              <div className="text-center pt-1.5">
                <Link
                  to="/register"
                  className="text-[13.5px] font-medium text-white/95 underline hover:text-[#D8F2F1] transition-colors"
                >
                  Back to registration
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalDetails;
